import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import {
  Star,
  MessageSquare,
  Mail,
  Clock,
  ExternalLink,
  Loader2,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  MousePointer,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Reviews() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState({
    enabled: false,
    hours_after_appointment: 2,
    doctoralia_url: '',
    google_url: '',
    message_template: '¡Hola {nombre}! Gracias por visitarnos. Tu opinión es muy importante para nosotros. ¿Podrías dejarnos una reseña?',
    send_via_whatsapp: true,
    send_via_email: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, statsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/clinic/${clinicId}/review-settings`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/reviews-stats`, { headers }),
        fetch(`${API_URL}/api/clinic/${clinicId}/reviews-log?limit=20`, { headers })
      ]);

      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (logsRes.ok) {
        setLogs(await logsRes.json());
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const clinicId = localStorage.getItem('clinicId');
      const token = localStorage.getItem('token');

      const res = await fetch(`${API_URL}/api/clinic/${clinicId}/review-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        setMessage({ type: 'success', text: '¡Configuración guardada!' });
      } else {
        setMessage({ type: 'error', text: 'Error al guardar' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Error de conexión' });
    }
    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Star className="w-7 h-7 text-yellow-500" />
            Solicitud de Reseñas
          </h1>
          <p className="text-gray-500 mt-1">
            Pide reseñas automáticamente en Doctoralia y Google después de cada cita
          </p>
        </div>

        {/* Message */}
        {message.text && (
          <div className={`p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
            {message.text}
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Send className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.total_sent}</p>
                    <p className="text-xs text-gray-500">Solicitudes enviadas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <MousePointer className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.total_clicked}</p>
                    <p className="text-xs text-gray-500">Clicks en links</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.click_rate}%</p>
                    <p className="text-xs text-gray-500">Tasa de click</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">
                      {stats.doctoralia_clicks + stats.google_clicks}
                    </p>
                    <p className="text-xs text-gray-500">Reseñas potenciales</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Settings */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Configuración</CardTitle>
                <CardDescription>Personaliza cuándo y cómo pedir reseñas</CardDescription>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* URLs */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <img src="https://www.doctoralia.com.mx/favicon.ico" alt="Doctoralia" className="w-4 h-4" />
                  URL de Doctoralia
                </label>
                <Input
                  value={settings.doctoralia_url || ''}
                  onChange={(e) => setSettings({ ...settings, doctoralia_url: e.target.value })}
                  placeholder="https://www.doctoralia.com.mx/dr-tu-nombre/opiniones"
                  className="bg-slate-700/50 border-slate-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Encuentra tu URL en tu perfil de Doctoralia → sección "Opiniones"
                </p>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  URL de Google Reviews
                </label>
                <Input
                  value={settings.google_url || ''}
                  onChange={(e) => setSettings({ ...settings, google_url: e.target.value })}
                  placeholder="https://g.page/r/tu-negocio/review"
                  className="bg-slate-700/50 border-slate-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Google Business → "Recibir más reseñas" → Copia el link
                </p>
              </div>
            </div>

            {/* Timing */}
            <div>
              <label className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Enviar solicitud después de la cita
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min="1"
                  max="72"
                  value={settings.hours_after_appointment}
                  onChange={(e) => setSettings({ ...settings, hours_after_appointment: parseInt(e.target.value) || 2 })}
                  className="w-24 bg-slate-700/50 border-slate-600"
                />
                <span className="text-gray-400">horas</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Recomendado: 2-4 horas después de la cita para mejor respuesta
              </p>
            </div>

            {/* Message Template */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Mensaje personalizado</label>
              <textarea
                value={settings.message_template}
                onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
                rows={3}
                className="w-full bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-white resize-none"
                placeholder="Escribe tu mensaje..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Variables disponibles: {'{nombre}'} - Nombre del paciente
              </p>
            </div>

            {/* Channels */}
            <div>
              <label className="text-sm text-gray-400 mb-3 block">Canales de envío</label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg cursor-pointer">
                  <Switch
                    checked={settings.send_via_whatsapp}
                    onCheckedChange={(checked) => setSettings({ ...settings, send_via_whatsapp: checked })}
                  />
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  <span>WhatsApp</span>
                </label>
                
                <label className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg cursor-pointer">
                  <Switch
                    checked={settings.send_via_email}
                    onCheckedChange={(checked) => setSettings({ ...settings, send_via_email: checked })}
                  />
                  <Mail className="w-5 h-5 text-blue-400" />
                  <span>Email</span>
                </label>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Vista previa del mensaje</label>
              <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                <p className="text-white whitespace-pre-wrap">
                  {settings.message_template.replace('{nombre}', 'Juan')}
                </p>
                {(settings.doctoralia_url || settings.google_url) && (
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <p className="text-white mb-2">⭐ Déjanos tu reseña:</p>
                    {settings.doctoralia_url && (
                      <p className="text-blue-400 flex items-center gap-2">
                        📋 Doctoralia: <span className="underline">{settings.doctoralia_url.substring(0, 40)}...</span>
                      </p>
                    )}
                    {settings.google_url && (
                      <p className="text-blue-400 flex items-center gap-2">
                        🔍 Google: <span className="underline">{settings.google_url.substring(0, 40)}...</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Button
              onClick={saveSettings}
              disabled={saving}
              className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
              Guardar Configuración
            </Button>
          </CardContent>
        </Card>

        {/* History */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Historial de Solicitudes</CardTitle>
            <CardDescription>Últimas solicitudes de reseñas enviadas</CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length > 0 ? (
              <div className="space-y-3">
                {logs.map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        log.clicked ? 'bg-green-500/20' : 'bg-slate-600/50'
                      }`}>
                        <Star className={`w-5 h-5 ${log.clicked ? 'text-green-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-white">{log.patient_name || 'Paciente'}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(log.sent_at).toLocaleDateString('es-MX', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {log.clicked ? (
                        <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded-full flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" />
                          {log.platform_clicked || 'Abierto'}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 bg-slate-600/50 text-gray-400 rounded-full">
                          Pendiente
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                No hay solicitudes enviadas aún
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
